import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import RcTooltip from "rc-tooltip";
import moment from "moment";
import { ExternalLink, X, ChevronRight, Database, Layers, Clock, TriangleAlert, Blocks } from "lucide-react";
import Load from "../../components/loadAction";
import { getChartData, pipelineRunInfo } from "../../utils/realtimeEventHandle";
import StackedBarChart from "../../realtime/stackedBar";
import {
  AiInsightPanelParagraphs,
  AiInsightPanelRecommendation,
  fetchIncidentAiInsights,
  getIncidentAiInsightsRequest
} from "./incidentAiInsightContent";

const ChartComponent = React.lazy(() => import("../../realtime/chartcomponent"));

const TIME_RANGES = ["1D", "5D", "1M", "3M", "6M"];
const RANGE_OFFSETS = {
  "1D": [0, "days"],
  "5D": [5, "days"],
  "1M": [1, "months"],
  "3M": [3, "months"],
  "6M": [6, "months"]
};
const CHART_BG = "#0A0824";

const formatRunDuration = (millis) => {
  millis = parseInt(millis, 10);
  if (!Number.isFinite(millis)) return "";
  const seconds = Math.floor((millis / 1000) % 60);
  const minutes = Math.floor((millis / (1000 * 60)) % 60);
  const hours = Math.floor((millis / (1000 * 60 * 60)) % 24);
  const days = Math.floor(millis / (1000 * 60 * 60 * 24));
  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const toRunChartPoint = (run, index, stageData) => {
  const stages = stageData?.[run.source_run_id] || [];
  const startSource = run.run_start_time || run.source_run_id || "";
  const matched = String(startSource).match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  const start = matched ? `${matched[1]}T${matched[2]}` : startSource;
  return {
    id: index,
    run_id: run.source_run_id,
    time: start,
    value: Number(run.duration) || 0,
    value_format: formatRunDuration(run.duration),
    stages: stages.length
      ? stages
      : [{
          stage_name: run.source_run_id || "Run",
          stage_status_text: run.run_status_text || "Succeeded",
          duration: run.duration,
        }],
    hasError: run.run_status_text === "Failed" || run.run_status_text === "stage_failure_run_success",
  };
};

const getRangeDates = (range, anchorDate) => {
  const anchor = moment(anchorDate || undefined);
  const endDate = (anchor.isValid() ? anchor : moment()).format("YYYY-MM-DD");
  if (!RANGE_OFFSETS[range]) {
    return { startDate: moment(endDate).subtract(29, "days").format("YYYY-MM-DD"), endDate };
  }
  const [amount, unit] = RANGE_OFFSETS[range];
  return { startDate: moment(endDate).subtract(amount, unit).format("YYYY-MM-DD"), endDate };
};

const renderBreadcrumbText = (text) => {
  if (!text) return null;
  if (text.length > 30) {
    const truncated = text.substring(0, 30) + "...";
    return (
      <RcTooltip placement="top" overlay={<span>{text}</span>}>
        <span className="cursor-pointer">{truncated}</span>
      </RcTooltip>
    );
  }
  return <span>{text}</span>;
};

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-white/60">This metric chart could not be drawn.</div>;
    }
    return this.props.children;
  }
}

const chartTime = (stamp) => {
  if (typeof stamp !== "string") return stamp;
  const trimmed = stamp.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(trimmed)) return trimmed.replace(" ", "T");
  return trimmed;
};

const toChartSeries = (series) => {
  if (!series || typeof series !== "object") return null;
  const value = Array.isArray(series.value) ? series.value : Array.isArray(series.values) ? series.values : [];
  const time = (Array.isArray(series.time) ? series.time : []).map(chartTime);
  if (!time.length || !value.length) return null;
  return {
    ...series,
    time,
    value,
    last_processed_time: typeof series.last_processed_time === "string" ? series.last_processed_time : undefined,
  };
};

const chartVars = {
  "--layout-bg": CHART_BG,
  "--hover-bg": "rgba(255, 255, 255, 0.08)",
  "--dropdown-menu-bg": "#1E1A45",
  "--realtime-toolbar-icon-color": "rgba(255, 255, 255, 0.75)",
  "--realtime-chart-btn-grey-color": "rgba(255, 255, 255, 0.85)",
  "--realtime-chart-btn-grey-border": "rgba(255, 255, 255, 0.18)",
  "--realtime-chart-btn-grey-icon": "rgba(255, 255, 255, 0.55)",
  "--realtime-chart-filter-border": "rgba(255, 255, 255, 0.18)",
  "--realtime-chart-filter-bg": "rgba(255, 255, 255, 0.04)",
  "--realtime-chart-dropdown-shadow": "0 4px 12px rgba(0, 0, 0, 0.5)",
  "--realtime-chart-separator-bg": "rgba(255, 255, 255, 0.12)",
  "--realtime-chart-stat-caption-color": "rgba(255, 255, 255, 0.55)",
  "--realtime-chart-stat-value-color": "rgba(255, 255, 255, 0.9)",
  "--btn-outline-grey-color": "rgba(255, 255, 255, 0.85)",
  "--btn-outline-grey-border": "rgba(255, 255, 255, 0.18)",
  "--btn-outline-grey-bg": "transparent",
  "--btn-outline-grey-hover-bg": "rgba(255, 255, 255, 0.08)"
};

const pickMetricSeries = (chartDetails, metricsId, attributeId, datasetId, metricsName) => {
  const entries = Object.entries(chartDetails || {});
  const metricKey = metricsId != null && metricsId !== "" ? String(metricsId) : "";
  const includesMetric = ([key]) => {
    if (!metricKey) return true;
    return key.includes(`_${metricKey}_`) || key.endsWith(`_${metricKey}`);
  };
  let matched = entries.filter(includesMetric);
  if (attributeId != null && attributeId !== "") {
    const attributeSeries = matched.filter(([key]) => key.endsWith("_attr") && key.startsWith(`${attributeId}_`));
    if (attributeSeries.length) matched = attributeSeries;
  } else if (datasetId != null && datasetId !== "") {
    const datasetSeries = matched.filter(([key]) => key.endsWith("_dt") && key.startsWith(`${datasetId}_`));
    if (datasetSeries.length) matched = datasetSeries;
  }
  if (metricsName) {
    const named = matched.filter(([, series]) => series && series.name === metricsName);
    if (named.length) matched = named;
  }
  return matched[0] ? matched[0][1] : null;
};

export default class IncidentMonitorModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: "",
      series: null,
      runs: [],
      runChart: [],
      runSummary: {},
      selectedTimeRange: null,
      selectedChartType: "Area",
      chartWindow: null,
      liked: null,
      insights: null,
      insightsLoading: false,
      insightsError: null
    };
    this.requestId = 0;
    this.insightsRequestId = 0;
  }

  handleChartTypeChange = (type) => {
    if (!type || type === this.state.selectedChartType) return;
    this.setState({ selectedChartType: type });
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.load();
      this.loadInsights();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isOpen) return;
    const opened = this.props.isOpen && !prevProps.isOpen;
    const changed = prevProps.monitorData !== this.props.monitorData;
    if (opened || changed) {
      this.setState({ selectedTimeRange: null, selectedChartType: "Area", liked: null, series: null, runs: [], runChart: [], runSummary: {}, error: "", chartWindow: null }, () => {
        this.load();
        this.loadInsights();
      });
    }
  }

  componentWillUnmount() {
    this.requestId += 1;
    this.insightsRequestId += 1;
  }

  handleTimeRangeChange = (range) => {
    this.setState({ selectedTimeRange: range }, this.load);
  };

  handleLikeToggle = (type) => {
    this.setState((prev) => ({ liked: prev.liked === type ? null : type }));
  };

  loadInsights = async () => {
    const requestId = ++this.insightsRequestId;
    const request = getIncidentAiInsightsRequest(this.props.incidentItem || this.props.monitorData);
    this.setState({ insights: null, insightsLoading: true, insightsError: null });
    const result = await fetchIncidentAiInsights(request);
    if (requestId !== this.insightsRequestId) return;
    this.setState({
      insights: result.data || null,
      insightsLoading: false,
      insightsError: result.data ? null : result.error
    });
  };

  load = async () => {
    const { isOpen, monitorData } = this.props;
    if (!isOpen || !monitorData) return;
    const requestId = ++this.requestId;
    const chartWindow = getRangeDates(this.state.selectedTimeRange, monitorData.date);
    this.setState({ loading: true, error: "", series: null, runs: [], runChart: [], runSummary: {}, chartWindow });
    try {
      if (monitorData.type === "PIPELINE") {
        await this.loadRuns(requestId, chartWindow);
      } else {
        await this.loadMetricChart(requestId, chartWindow);
      }
    } catch (error) {
      if (requestId !== this.requestId) return;
      this.setState({ loading: false, error: "Failed to load the chart." });
    }
  };

  loadMetricChart = async (requestId, chartWindow) => {
    const { monitorData, selectedEnvId, teamId, userId, browserTabId, browserSessionId } = this.props;
    const numericId = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const attributeId = numericId(monitorData.attribute_id);
    const datasetId = numericId(monitorData.dataset_id);
    const integrationId = numericId(monitorData.integration_id);
    if (datasetId == null || integrationId == null) {
      this.setState({ loading: false, error: "Chart data is not available for this incident." });
      return;
    }
    // Row Count and other dataset metrics (id_type 1) use the monitor's DatasetandAttribute
    // call. type "Dataset" and an empty meta_data_id list both make the API return Bad Request.
    const datasetLevel = monitorData.id_type === 1 || attributeId == null;
    const requestParams = {
      tab_type: 1,
      tenant_id: teamId,
      env_id: selectedEnvId || monitorData.env_id,
      user_id: userId,
      start_date: chartWindow.startDate,
      end_date: chartWindow.endDate,
      integration_id: integrationId,
      data_set_id: datasetId,
      meta_data_id: datasetLevel ? [datasetId] : [attributeId],
      menu_type: "monitor",
      browser_tab_id: browserTabId,
      session_id: browserSessionId,
      additional_info: { id: datasetId, type: "dataset" },
      selected_ids: datasetLevel ? [datasetId] : [attributeId],
      type: datasetLevel ? "DatasetandAttribute" : "Attribute",
      ids_type: "dataset",
      selected_period: "default",
    };
    if (monitorData.is_child && !datasetLevel) requestParams.is_child = true;
    let response = await getChartData(requestParams);
    if (requestId !== this.requestId) return;
    if (requestParams.is_child && response?.code !== 200) {
      delete requestParams.is_child;
      response = await getChartData(requestParams);
      if (requestId !== this.requestId) return;
    }
    if (!response || response.code !== 200) {
      this.setState({
        loading: false,
        series: null,
        error: response?.message && response.message !== "Bad Request"
          ? response.message
          : "No data available for the selected range.",
      });
      return;
    }
    const details = response?.response?.chart_details || response?.response?.chart_data || {};
    const series = toChartSeries(pickMetricSeries(
      details,
      monitorData.metrics_id,
      datasetLevel ? null : attributeId,
      datasetId,
      monitorData.metrics_name
    ));
    this.setState({
      loading: false,
      series,
      error: series ? "" : "No data available for the selected range.",
    });
  };

  loadRuns = async (requestId, chartWindow) => {
    const { monitorData, selectedEnvId, teamId, userId, browserTabId, browserSessionId } = this.props;
    const numericId = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const pipelineId = numericId(monitorData.pipeline_id);
    const integrationId = numericId(monitorData.p_integration_id);
    if (pipelineId == null || integrationId == null) {
      this.setState({ loading: false, error: "Pipeline run details are not available for this incident." });
      return;
    }
    const response = await pipelineRunInfo({
      tenant_id: teamId,
      pipeline_id: pipelineId,
      p_integration_id: integrationId,
      filter_date: chartWindow.endDate,
      menu_type: "monitor",
      env_id: selectedEnvId || monitorData.env_id,
      session_id: browserSessionId,
      user_id: userId,
      tab_type: 3,
      selected_ids: [pipelineId],
      ids_type: "runs_info",
      browser_tab_id: browserTabId,
    });
    if (requestId !== this.requestId) return;
    if (response && response.code !== undefined && response.code !== 200 && !response.data && !response.response) {
      this.setState({ loading: false, runs: [], runChart: [], error: "Pipeline run details are not available for this incident." });
      return;
    }
    const payload = response?.data || response?.response || {};
    const runs = Array.isArray(payload.run_data) ? payload.run_data : [];
    const runChart = runs.map((run, index) => toRunChartPoint(run, index, payload.stage_data || {}));
    this.setState({
      loading: false,
      runs,
      runChart,
      runSummary: payload.run_summary || {},
      error: runChart.length ? "" : "No runs were returned for this pipeline.",
    });
  };

  render() {
    const { isOpen, onClose, onOpenFullMonitor, monitorData, selectedEnvName } = this.props;
    if (!isOpen || !monitorData) return null;
    const isPipeline = monitorData.type === "PIPELINE";
    const title = isPipeline
      ? (monitorData.pipeline_name || "Pipeline runs")
      : (monitorData.attribute_name || monitorData.metrics_name || "Metric chart");
    const envName = monitorData.env_name || selectedEnvName || "Environment";
    const integrationName = monitorData.integration_name || monitorData.pipeline_integration_name || "Integration";
    const datasetName = isPipeline
      ? (monitorData.pipeline_name || "Pipeline")
      : (monitorData.dataset_name || "Dataset");
    const profiledTime = this.state.series?.last_processed_time || monitorData.incident_time || monitorData.date || "";
    const { loading, error, series, runChart, runSummary, chartWindow, insights, insightsLoading, insightsError, selectedChartType } = this.state;
    const waitingForSeries = !isPipeline && !series && !error;
    const hasInsights = Boolean(insights && (insights.title || insights.paragraphs?.length || insights.recommendations?.length || insights.evidence?.length));

    return ReactDOM.createPortal(
      <div
        className="tw-fixed tw-inset-0 tw-z-[9999] tw-backdrop-blur-[10px] tw-flex tw-items-center tw-justify-center tw-p-[50px_24px] tw-overflow-y-auto"
        style={{ background: "rgba(9, 5, 32, 0.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget && onClose) onClose();
        }}
      >
        <style>{`
          @keyframes modalPopFromCenter {
            0% { opacity: 0; transform: scale(0.90); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}</style>
        <div
          className="tw-w-full tw-max-w-[1180px] tw-h-[calc(100vh-100px)] tw-flex tw-flex-col tw-rounded-[24px] tw-shadow-[0_40px_100px_rgba(4,1,20,0.75)] tw-overflow-hidden tw-text-white tw-bg-[#14113A]/65"
          style={{
            border: "1px solid rgba(98, 67, 255, 0.3)",
            animation: "modalPopFromCenter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards"
          }}
        >
          <div className="tw-flex-none tw-flex tw-items-center tw-gap-[14px] tw-p-5 tw-pb-0">
            <h2 className="tw-m-0 tw-text-2xl tw-font-semibold tw-flex tw-items-center tw-gap-2 tw-text-[#CCCCCC]">
              <span>{title}</span>
            </h2>
            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-[8px]">
              <button
                type="button"
                onClick={() => onOpenFullMonitor && onOpenFullMonitor(monitorData)}
                className="tw-inline-flex tw-items-center tw-gap-[8px] tw-px-[14px] tw-py-[7px] tw-rounded-[8px] tw-text-[13px] tw-font-medium tw-text-white tw-transition-all tw-duration-200 cursor-pointer"
                style={{ background: "rgba(255, 255, 255, 0.08)", border: "1px solid rgba(255, 255, 255, 0.18)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.16)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.18)";
                }}
              >
                <span>Go to Monitor</span>
                <ExternalLink className="tw-w-[14px] tw-h-[14px]" />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="tw-w-[32px] tw-h-[32px] tw-rounded-[9px] tw-flex tw-items-center tw-justify-center tw-text-white/60 hover:tw-text-white hover:tw-bg-white/10 tw-border-0 tw-bg-transparent tw-transition-colors cursor-pointer"
              >
                <X className="tw-w-[18px] tw-h-[18px]" />
              </button>
            </div>
          </div>

          <div className="tw-flex-none tw-flex tw-items-center tw-gap-[8px] tw-flex-wrap tw-px-5 tw-pt-3 tw-text-sm tw-text-white/60">
            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Database className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(envName)}
            </span>
            <ChevronRight className="tw-w-[12px] tw-h-[12px] tw-opacity-45" />
            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Blocks className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(integrationName)}
            </span>
            <ChevronRight className="tw-w-[12px] tw-h-[12px] tw-opacity-45" />
            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Layers className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(datasetName)}
            </span>
            {profiledTime ? (
              <span className="tw-ml-auto tw-text-white/45 tw-inline-flex tw-items-center tw-gap-[7px]">
                <Clock className="tw-w-[14px] tw-h-[14px]" />
                <span>
                  Last profiled at <b className="tw-text-white/80 tw-font-medium">{profiledTime}</b>
                </span>
              </span>
            ) : null}
          </div>

          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-y-auto">
            <div
              className="tw-mx-5 tw-mt-[14px] tw-mb-0 tw-rounded-[12px] tw-p-4 tw-flex tw-flex-col"
              style={{ background: CHART_BG, border: "1px solid rgba(255, 255, 255, 0.1)", flex: "0 0 auto", minHeight: 0, overflow: "hidden" }}
            >
              <div className="tw-relative tw-w-full tw-min-w-0 tw-flex tw-flex-col" style={{ flex: "0 0 auto" }}>
                {loading || waitingForSeries ? (
                  <Load />
                ) : error ? (
                  <div className="tw-text-sm tw-text-white/60 tw-px-6 tw-text-center">{error}</div>
                ) : isPipeline ? (
                  <div className="tw-w-full tw-h-full tw-min-h-0 tw-min-w-0 tw-flex tw-flex-col" style={chartVars}>
                    <div className="tw-flex-none tw-flex tw-flex-row tw-flex-nowrap tw-items-center tw-pb-3 tw-text-[13px] tw-leading-5" style={{ gap: 40 }}>
                      <div className="tw-min-w-0">
                        <span className="tw-text-white/55">Suc. Rate: </span>
                        <strong className="tw-font-medium tw-text-[#3DDC97]">{runSummary.success_rate != null ? `${Number(runSummary.success_rate).toFixed(1)}%` : "—"}</strong>
                      </div>
                      <div className="tw-min-w-0">
                        <span className="tw-text-white/55">Avg Duration: </span>
                        <strong className="tw-font-medium tw-text-white/90">{formatRunDuration(runSummary.avg_duration) || "—"}</strong>
                      </div>
                      <div className="tw-min-w-0">
                        <span className="tw-text-white/55">Cur. Duration: </span>
                        <strong id="cur_run_duration" className="tw-font-medium tw-text-white/90">{runChart[0]?.value_format || "—"}</strong>
                      </div>
                    </div>
                    <div className="tw-relative tw-w-full" style={{ height: 320 }}>
                      <StackedBarChart
                        theme="dark"
                        showIdentity={false}
                        showStats={false}
                        fitWidth
                        runId={runChart[0]?.run_id || ""}
                        runDuration={runChart[0]?.value_format || ""}
                        avg_duration={formatRunDuration(runSummary.avg_duration)}
                        success_rate={runSummary.success_rate != null ? Number(runSummary.success_rate).toFixed(1) : ""}
                        connectorIcon="connector_icon_airflow"
                        jobName={monitorData.pipeline_name || ""}
                        pipelineName={monitorData.pipeline_integration_name || monitorData.integration_name || ""}
                        data={runChart}
                      />
                    </div>
                  </div>
                ) : series ? (
                  <div className="qd-rt_chart-container" style={{ ...chartVars, position: "relative", height: 320, overflow: "hidden" }}>
                    <div style={{ flex: 1, minWidth: 0, width: "100%", height: "100%", boxSizing: "border-box" }}>
                      <ChartErrorBoundary key={`${monitorData.metrics_id || "metric"}-${chartWindow?.startDate || ""}-${selectedChartType}`}>
                        <Suspense fallback={<Load />}>
                          <div className="qd-rt_chart-widget" style={{ width: "100%", height: "100%" }}>
                            <ChartComponent
                              chartType={selectedChartType}
                              onChartTypeChange={this.handleChartTypeChange}
                              chartIndex={0}
                              chartColIndex={0}
                              lastProcessedDate={series.last_processed_time}
                              selectedDatasetName={monitorData.metrics_name}
                              selectedDatasetAttrName={monitorData.attribute_name || ""}
                              realdata={series}
                              dataType={monitorData.attribute_id ? "Attribute" : "Dataset"}
                              timeGranularitySelected="default"
                              theme="dark"
                              darkMode
                              chartBackground={CHART_BG}
                            />
                          </div>
                        </Suspense>
                      </ChartErrorBoundary>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {isPipeline ? null : <div className="tw-flex-none tw-flex tw-items-center tw-gap-[4px] tw-px-5 tw-pt-2 tw-pb-0" style={{ position: "relative", zIndex: 2 }}>
              {TIME_RANGES.map((range) => {
                const isActive = this.state.selectedTimeRange === range;
                return (
                  <button
                    key={range}
                    type="button"
                    onClick={() => this.handleTimeRangeChange(range)}
                    className="tw-min-w-[40px] tw-px-[12px] tw-py-[6px] tw-rounded-[8px] tw-text-xs tw-font-medium tw-border-0 tw-transition-colors cursor-pointer"
                    style={{
                      background: isActive ? "rgba(255, 255, 255, 0.16)" : "transparent",
                      color: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.6)"
                    }}
                  >
                    {range}
                  </button>
                );
              })}
              {chartWindow && chartWindow.endDate ? (
                <span className="tw-ml-[8px] tw-text-xs tw-text-white/50 tw-whitespace-nowrap">
                  {this.state.selectedTimeRange === TIME_RANGES[0]
                    ? `${moment(chartWindow.startDate).format("DD-MM-YYYY")} (GMT)`
                    : `${moment(chartWindow.startDate).format("DD-MMM-YYYY")} - ${moment(chartWindow.endDate).format("DD-MMM-YYYY")} (GMT)`}
                </span>
              ) : null}
            </div>}

            <div
              className="tw-flex-none tw-m-5 tw-p-[1px] tw-rounded-[12px]"
              style={{ background: "rgba(98, 67, 255, 0.3)" }}
            >
              <div
                className="tw-rounded-[12px] tw-p-4"
                style={{ background: "linear-gradient(135deg, #250E3C -85%, #280B3D 78%)" }}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <g clipPath="url(#clip_incident_monitor_insight)">
                        <path
                          d="M16.6665 1.66667V5M18.3332 3.33333H14.9999M9.18071 2.345C9.21642 2.15384 9.31786 1.98118 9.46746 1.85693C9.61706 1.73269 9.80541 1.66467 9.99988 1.66467C10.1943 1.66467 10.3827 1.73269 10.5323 1.85693C10.6819 1.98118 10.7833 2.15384 10.819 2.345L11.6949 6.97667C11.7571 7.30596 11.9171 7.60885 12.1541 7.84581C12.391 8.08277 12.6939 8.2428 13.0232 8.305L17.6549 9.18084C17.846 9.21654 18.0187 9.31798 18.1429 9.46758C18.2672 9.61719 18.3352 9.80553 18.3352 10C18.3352 10.1945 18.2672 10.3828 18.1429 10.5324C18.0187 10.682 17.846 10.7835 17.6549 10.8192L13.0232 11.695C12.6939 11.7572 12.391 11.9172 12.1541 12.1542C11.9171 12.3912 11.7571 12.694 11.6949 13.0233L10.819 17.655C10.7833 17.8462 10.6819 18.0188 10.5323 18.1431C10.3827 18.2673 10.1943 18.3353 9.99988 18.3353C9.80541 18.3353 9.61706 18.2673 9.46746 18.1431C9.31786 18.0188 9.21642 17.8462 9.18071 17.655L8.30488 13.0233C8.24267 12.694 8.08265 12.3912 7.84569 12.1542C7.60873 11.9172 7.30584 11.7572 6.97655 11.695L2.34488 10.8192C2.15372 10.7835 1.98106 10.682 1.85681 10.5324C1.73256 10.3828 1.66455 10.1945 1.66455 10C1.66455 9.80553 1.73256 9.61719 1.85681 9.46758C1.98106 9.31798 2.15372 9.21654 2.34488 9.18084L6.97655 8.305C7.30584 8.2428 7.60873 8.08277 7.84569 7.84581C8.08265 7.60885 8.24267 7.30596 8.30488 6.97667L9.18071 2.345ZM4.99988 16.6667C4.99988 17.5871 4.25369 18.3333 3.33321 18.3333C2.41274 18.3333 1.66655 17.5871 1.66655 16.6667C1.66655 15.7462 2.41274 15 3.33321 15C4.25369 15 4.99988 15.7462 4.99988 16.6667Z"
                          stroke="url(#paint_incident_monitor_insight)"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <linearGradient id="paint_incident_monitor_insight" x1="4.09548" y1="15.8724" x2="15.9216" y2="4.13483" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#6243FF" />
                          <stop offset="0.5" stopColor="#8B6CFF" />
                          <stop offset="1" stopColor="#C4B5FD" />
                        </linearGradient>
                        <clipPath id="clip_incident_monitor_insight">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="tw-text-[18px] tw-font-semibold tw-text-white">QualdoAI Insights</span>
                    {insights?.badge ? (
                      <div className="tw-bg-[#53030C] tw-border tw-border-solid tw-border-[#F64E60] tw-rounded-[6px] tw-py-[3px] tw-px-2 tw-text-xs tw-font-medium tw-text-[#F7BAC1] tw-inline-flex tw-items-center tw-gap-1 tw-leading-none">
                        <TriangleAlert className="tw-w-3 tw-h-3" />
                        <span>{insights.badge}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-[2px] tw-text-[#94979C]">
                    <button
                      type="button"
                      onClick={() => this.handleLikeToggle("up")}
                      className="tw-p-1 tw-w-[32px] tw-h-[32px] tw-border-0 tw-bg-transparent hover:tw-text-white hover:!tw-bg-white/10 tw-rounded-full tw-transition-colors cursor-pointer"
                      style={{ color: this.state.liked === "up" ? "#FFFFFF" : "#94979C" }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                        {this.state.liked === "up" ? (
                          <path d="M709.23-140H313.08v-480l265.38-263.84L612.3-850q6.24 6.23 10.35 16.5 4.12 10.27 4.12 19.35V-804L584.3-620h243.39q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.31 13.46t-3.92 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140ZM253.08-620v480H100v-480h153.08Z" />
                        ) : (
                          <path d="M709.23-140H288.46v-480l265.39-263.84L587.69-850q6.23 6.23 10.35 16.5 4.11 10.27 4.11 19.35V-804l-42.46 184h268q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.62 13.46-1.61 7.23-3.61 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140Zm-360.77-60h360.77q4.23 0 8.65-2.31 4.43-2.31 6.74-7.69L840-480v-67.69q0-5.39-3.46-8.85t-8.85-3.46H483.85L534-779.23 348.46-594.46V-200Zm0-394.46V-200v-394.46Zm-60-25.54v60H160v360h128.46v60H100v-480h188.46Z" />
                        )}
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => this.handleLikeToggle("down")}
                      className="tw-p-1 tw-w-[32px] tw-h-[32px] tw-border-0 tw-bg-transparent hover:tw-text-white hover:!tw-bg-white/10 tw-rounded-full tw-transition-colors cursor-pointer"
                      style={{ color: this.state.liked === "down" ? "#FFFFFF" : "#94979C" }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                        <g transform="rotate(180 480 -480)">
                          {this.state.liked === "down" ? (
                            <path d="M709.23-140H313.08v-480l265.38-263.84L612.3-850q6.24 6.23 10.35 16.5 4.12 10.27 4.12 19.35V-804L584.3-620h243.39q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.31 13.46t-3.92 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140ZM253.08-620v480H100v-480h153.08Z" />
                          ) : (
                            <path d="M709.23-140H288.46v-480l265.39-263.84L587.69-850q6.23 6.23 10.35 16.5 4.11 10.27 4.11 19.35V-804l-42.46 184h268q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.62 13.46-1.61 7.23-3.61 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140Zm-360.77-60h360.77q4.23 0 8.65-2.31 4.43-2.31 6.74-7.69L840-480v-67.69q0-5.39-3.46-8.85t-8.85-3.46H483.85L534-779.23 348.46-594.46V-200Zm0-394.46V-200v-394.46Zm-60-25.54v60H160v360h128.46v60H100v-480h188.46Z" />
                          )}
                        </g>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="tw-pl-[28px]">
                  {insightsLoading ? (
                    <p className="tw-text-sm tw-text-[#E2EAFE]/85 tw-mb-0 tw-leading-relaxed">Loading AI insights...</p>
                  ) : insightsError ? (
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                      <p className="tw-text-sm tw-text-[#F7BAC1] tw-mb-0 tw-leading-relaxed">{insightsError}</p>
                      <button
                        type="button"
                        onClick={this.loadInsights}
                        className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-semibold tw-underline tw-text-[#8FB0FF] cursor-pointer"
                      >
                        Retry
                      </button>
                    </div>
                  ) : !hasInsights ? (
                    <p className="tw-text-sm tw-text-[#E2EAFE]/85 tw-mb-0 tw-leading-relaxed">
                      No AI insights available for this incident yet.
                    </p>
                  ) : (
                    <>
                      {insights.title ? (
                        <h4 className="tw-text-[16px] tw-font-semibold tw-text-white tw-mb-2 tw-leading-snug">{insights.title}</h4>
                      ) : null}
                      <AiInsightPanelParagraphs paragraphs={insights.paragraphs} />
                    </>
                  )}
                  {!insightsLoading && !insightsError && insights?.evidence?.length > 0 ? (
                    <div className="tw-bg-[#13161B7A] tw-border tw-border-solid tw-border-[#22262F] tw-rounded-[12px] tw-py-3 tw-px-4 tw-mt-3">
                      <AiInsightPanelRecommendation label="Evidence:" paragraphs={insights.evidence} spaced={false} />
                    </div>
                  ) : null}
                  {!insightsLoading && !insightsError && insights?.recommendations?.length > 0 ? (
                    <div className="tw-bg-[#13161B7A] tw-border tw-border-solid tw-border-[#22262F] tw-rounded-[12px] tw-py-3 tw-px-4 tw-mt-3">
                      <AiInsightPanelRecommendation paragraphs={insights.recommendations} spaced={false} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }
}
